// apps/web/src/features/commercial-proposals/components/CommercialProposalDrawerActions.tsx
//
// @aiPurpose the draft/published action set for the detail drawer (#1198).
//   DRAFT: Edit, Preview, Publish (NO PDF download). PUBLISHED: Preview,
//   Copy public link, Download PDF, Revoke (NO editable content affordance).
// @aiSecurity the published action set intentionally omits any content/photo
//   edit affordance — a published snapshot is immutable; the operator must
//   Revoke back to DRAFT first. Copy link sources `publicLink` from the
//   authenticated detail response (repeatable), never a one-time publish
//   payload. Edit / Publish / Revoke are gated on `commercialProposals.manage`
//   as frontend defense-in-depth; Preview / Copy link / Download PDF stay
//   open to read-only users as read/export affordances. Backend RBAC remains
//   authoritative — this gate only hides mutate controls a `.read`-only user
//   could not exercise anyway.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommercialProposalStatus, type CommercialProposalDetailResponse } from '@magimp/shared';

import { Button } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { usePublishCommercialProposal, useRevokeCommercialProposal } from '../queries.js';
import { downloadCommercialProposalPdf } from '../../../api/commercial-proposals.js';
import { buildPublicUrl } from '../publicLink.js';
import { triggerBrowserDownload } from '../download.js';

export function CommercialProposalDrawerActions({
  detail,
  canManage,
  onTogglePreview,
  onEdit,
}: {
  detail: CommercialProposalDetailResponse;
  canManage: boolean;
  onTogglePreview: () => void;
  onEdit: () => void;
}) {
  const { t } = useTranslation('commercialProposals');
  const showToast = useToastStore((s) => s.show);
  const { proposal, publicLink } = detail;
  const publish = usePublishCommercialProposal(proposal.id);
  const revoke = useRevokeCommercialProposal(proposal.id);
  const [downloading, setDownloading] = useState(false);
  const busy = publish.isPending || revoke.isPending || downloading;

  const isPublished = proposal.status === CommercialProposalStatus.PUBLISHED;

  const onCopyLink = async () => {
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(buildPublicUrl(publicLink));
      showToast({ tone: 'success', text: t('actions.copyLinkDone') });
    } catch {
      showToast({ tone: 'error', text: t('actions.copyLinkError') });
    }
  };

  const onDownloadPdf = async () => {
    setDownloading(true);
    try {
      const { blob, filename } = await downloadCommercialProposalPdf(proposal.id);
      triggerBrowserDownload(blob, filename ?? `${proposal.publicCode}.pdf`);
    } catch {
      showToast({ tone: 'error', text: t('actions.downloadError') });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      data-testid="commercial-proposals-drawer-actions"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        padding: '12px 20px',
        borderTop: '1px solid var(--border-1)',
      }}
    >
      <Button
        kind="secondary"
        size="sm"
        onClick={onTogglePreview}
        data-testid="commercial-proposals-preview-toggle"
      >
        {t('actions.preview')}
      </Button>

      {!isPublished && canManage && (
        <>
          <Button
            kind="secondary"
            size="sm"
            disabled={busy}
            onClick={onEdit}
            data-testid="commercial-proposals-edit"
          >
            {t('actions.edit')}
          </Button>
          <Button
            kind="primary"
            size="sm"
            disabled={busy}
            onClick={() =>
              publish.mutate(undefined, {
                onError: () => showToast({ tone: 'error', text: t('error') }),
              })
            }
            data-testid="commercial-proposals-publish"
          >
            {publish.isPending ? t('actions.publishing') : t('actions.publish')}
          </Button>
        </>
      )}

      {isPublished && (
        <>
          <Button
            kind="secondary"
            size="sm"
            disabled={busy || !publicLink}
            onClick={() => void onCopyLink()}
            data-testid="commercial-proposals-copy-link"
          >
            {t('actions.copyLink')}
          </Button>
          <Button
            kind="secondary"
            size="sm"
            disabled={busy}
            onClick={() => void onDownloadPdf()}
            data-testid="commercial-proposals-download-pdf"
          >
            {t('actions.downloadPdf')}
          </Button>
          {canManage && (
            <Button
              kind="secondary"
              size="sm"
              disabled={busy}
              onClick={() =>
                revoke.mutate(undefined, {
                  onError: () => showToast({ tone: 'error', text: t('error') }),
                })
              }
              data-testid="commercial-proposals-revoke"
            >
              {revoke.isPending ? t('actions.revoking') : t('actions.revoke')}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
